import React, { useCallback, useRef, useState } from 'react';
import RequiredReasonDialog from '../RequiredReasonDialog';
import { moneyOrAbsent } from '../../utils/moneyOrAbsent';
import { formatDateDayMonth } from '../../utils/dateFormat';
import { previewEditImpact } from '../../utils/timesheetsApi';
import { changesSummary } from './timesheetHistory';

/**
 * THE "reason on saving" gate of every MANAGER timesheet save (owner 2026-10-29: *"A, reason on
 * saving if theres a change from the already submitted timesheet/timesheets"*; approved mock,
 * dialogs 2 and 3). `run(change, send)`:
 *   1. asks the server what the pending save would change (`POST /edit-impact`, read-only);
 *   2. when it changes a SUBMITTED week, opens THE reason prompt (`RequiredReasonDialog`, the house
 *      reason-required dialog — Save is disabled until a reason is typed) listing each submitted
 *      week it reaches with its pay before → after; "Keep editing" resolves `{ cancelled: true }`
 *      and the caller KEEPS its drafts;
 *   3. sends the save with the reason (`send(reason)`; `undefined` when none was needed).
 * 🔒 THE SERVER IS THE GATE. The preview is a convenience: if it fails, or the week was submitted
 * between the preview and the save, the save's own 400 `edit_reason_required` (which names the
 * weeks) opens the same prompt and the save is sent again — never dropped, never sent blank.
 * A preview that says the save would leave a submitted week incomplete answers the server's own
 * refusal text at once (`problem`) instead of asking for a reason the server would then refuse.
 *
 * `describe` (optional) names the save in the prompt ("Main Unit · Mon 26 Oct · Unit wrap 17:00 → 19:00").
 * → `{ run, dialog }`: render `dialog` once.
 */
const REASON_MAX = 2000;

function weekOf(w) {
  return formatDateDayMonth(w.week_start);
}

export default function useEditReason({ unitName = () => null } = {}) {
  const [ask, setAsk] = useState(null); // { weeks, describe }
  const [reason, setReason] = useState('');
  const resolveRef = useRef(null);

  const prompt = useCallback((weeks, describe) => new Promise((resolve) => {
    resolveRef.current = resolve;
    setReason('');
    setAsk({ weeks, describe });
  }), []);
  const settle = (value) => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setAsk(null);
    if (r) r(value);
  };

  const run = useCallback(async (change, send, { describe } = {}) => {
    let given;
    let pre = null;
    try { pre = await previewEditImpact(change); } catch (e) { pre = null; }
    if (pre && pre.ok && pre.body && pre.body.requires_reason) {
      const blocked = (pre.body.submitted_weeks || []).find((w) => w.problem);
      if (blocked) {
        return { ok: false, body: { code: 'edit_unsubmittable', error: `${blocked.name}'s submitted week would no longer be complete and priced — reopen it to make this change.` } };
      }
      given = await prompt(pre.body.submitted_weeks, describe);
      if (given == null) return { cancelled: true };
    }
    let r = await send(given);
    if (r && !r.ok && r.body && r.body.code === 'edit_reason_required' && !given) {
      given = await prompt(r.body.submitted_weeks || [], describe);
      if (given == null) return { cancelled: true };
      r = await send(given);
    }
    return r;
  }, [prompt]);

  const weeks = (ask && ask.weeks) || [];
  const one = weeks.length === 1 ? weeks[0] : null;
  const message = ask ? (
    <div data-testid="edit-reason-body">
      <p>
        {one
          ? <>{one.name}&rsquo;s week of {weekOf(one)} was submitted{one.submitted_at ? ` on ${formatDateDayMonth(one.submitted_at)}` : ''}.</>
          : <>{ask.describe ? <>{ask.describe}. </> : null}This change reaches {weeks.length} submitted weeks.</>}
        {' '}Saving will update the times and pay, and mark {one ? 'the week' : 'each week'} &ldquo;Changed since submitted&rdquo;.
        {one ? null : ' Weeks not yet submitted just update.'}
      </p>
      {one ? (
        <div className="mt-3 rounded-md border border-gray-200 p-2 text-sm dark:border-gray-600" data-testid="edit-reason-changes">
          {changesSummary(one.changes, unitName).map((line) => (
            <p key={line} className="text-gray-700 dark:text-gray-200">{line}</p>
          ))}
          {one.before !== undefined && one.after !== undefined && (one.before != null || one.after != null) && (
            <p className="mt-1 flex justify-between gap-3 font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-testid="edit-reason-pay">
              <span>Week pay</span><span>{moneyOrAbsent(one.before)} → {moneyOrAbsent(one.after)}</span>
            </p>
          )}
        </div>
      ) : (
        <ul className="mt-3 max-h-40 overflow-y-auto rounded-md border border-gray-200 text-sm dark:border-gray-600" data-testid="edit-reason-weeks">
          {weeks.map((w) => (
            <li key={`${w.lineage_id}|${w.week_start}`} className="flex justify-between gap-3 border-b border-gray-200 px-2 py-1.5 last:border-b-0 dark:border-gray-600">
              <span className="text-gray-800 dark:text-gray-100">{w.name}{weeks.some((x) => x.week_start !== w.week_start) ? ` · w/c ${weekOf(w)}` : ''}</span>
              <span className="tabular-nums text-gray-600 dark:text-gray-300">
                {w.before != null || w.after != null ? `${moneyOrAbsent(w.before)} → ${moneyOrAbsent(w.after)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

  const dialog = (
    <RequiredReasonDialog
      open={!!ask}
      title={one ? 'This week has been submitted' : `This change affects ${weeks.length} submitted weeks`}
      message={message}
      reason={reason}
      onReasonChange={setReason}
      reasonLabel="Reason for the change (required)"
      reasonMaxLength={REASON_MAX}
      confirmLabel="Save change"
      cancelLabel="Keep editing"
      variant="save"
      initialFocus="cancel"
      onCancel={() => settle(null)}
      onConfirm={(trimmed) => settle(trimmed)}
    >
      {String(reason || '').trim() === '' ? (
        <p className="text-xs text-gray-600 dark:text-gray-400" data-testid="edit-reason-hint">
          A reason is needed because this changes {weeks.length === 1 ? 'a submitted week' : 'submitted weeks'}.
        </p>
      ) : null}
    </RequiredReasonDialog>
  );

  return { run, dialog };
}
